import { Button } from '@mui/material';
import { difference } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import { CustomerReportModel } from '../../../../../../Generated/Commodity-Management-Api';
import { useApplicationDispatch } from '../../../../../../Redux/ReduxStore';
import { setCustomerReportUserOverride } from '../../../../../../Redux/Slices/CommodityManagementSlice';
import { StoneXDivider, StoneXRow } from '../../../../../StoneX';
import { StoneXCheckbox, StoneXModal } from '../../../../../StoneXMui';
import { StoneXTransferList } from '../../../../../StoneXMui/StoneXTransferList/StoneXTransferList';
import {
  DAILY_POSITIONS_ALL_COLUMNS,
  DailyPositionsColumn,
  DailyPositionsReportConfiguration,
  DailyPositionsReportUserOverride,
} from './Types';

interface DailyPositionsReportSettingsModalProps {
  customerReport: CustomerReportModel;
  open: boolean;
  onClose: () => void;
  onSave: (customerReport: CustomerReportModel) => void;
}

function isKnownColumn(column: string): column is DailyPositionsColumn {
  return DAILY_POSITIONS_ALL_COLUMNS.includes(column as DailyPositionsColumn);
}

const COLUMN_LABELS: Record<DailyPositionsColumn, string> = {
  TradeDate: 'Trade Date',
  BudgetMonth: 'Budget Month',
  Commodity: 'Commodity',
  Side: 'Side',
  PricingStartDate: 'Pricing Start',
  PricingEndDate: 'Pricing End',
  VolumePerDay: 'Volume / Day',
  TotalVolume: 'Total Volume',
  Unit: 'Unit',
  Price: 'Price',
  TotalValue: 'Total Value',
  Counterparty: 'Counterparty',
};

export default function DailyPositionsReportSettingsModal(props: DailyPositionsReportSettingsModalProps) {
  const { customerReport, open, onClose, onSave } = props;
  const dispatch = useApplicationDispatch();

  const reportConfiguration = useMemo(
    () => (customerReport.configuration ? JSON.parse(customerReport.configuration) as DailyPositionsReportConfiguration : {} as DailyPositionsReportConfiguration),
    [customerReport],
  );

  const [userOverrides, setUserOverrides] = useState<DailyPositionsReportUserOverride | undefined>();

  const [inactiveColumns, setInactiveColumns] = useState<DailyPositionsColumn[]>([]);
  const [activeColumns, setActiveColumns] = useState<DailyPositionsColumn[]>([]);
  const [updatedActiveColumns, setUpdatedActiveColumns] = useState<DailyPositionsColumn[] | undefined>();
  const [groupByCommodity, setGroupByCommodity] = useState<boolean>(false);

  const columnOrderMap = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    DAILY_POSITIONS_ALL_COLUMNS.forEach((column, index) => (map[column] = index));
    return map;
  }, []);

  useEffect(() => {
    const parsedOverrides = customerReport.userOverrides
      ? (JSON.parse(customerReport.userOverrides) as DailyPositionsReportUserOverride)
      : undefined;
    setUserOverrides(parsedOverrides);
  }, [customerReport]);

  useEffect(() => {
    const availableColumnsList = (reportConfiguration.columns?.available ?? DAILY_POSITIONS_ALL_COLUMNS).filter(isKnownColumn);
    const resolvedActiveColumns = (userOverrides?.columns?.active ?? reportConfiguration.columns?.active ?? availableColumnsList).filter(isKnownColumn);

    setInactiveColumns(difference(availableColumnsList, resolvedActiveColumns));
    setActiveColumns(resolvedActiveColumns);
    setUpdatedActiveColumns(undefined);
    setGroupByCommodity(userOverrides?.behavior?.groupByCommodity ?? reportConfiguration.behavior?.groupByCommodity ?? false);
  }, [reportConfiguration, userOverrides]);

  function onTransferListUpdate(_inactive: DailyPositionsColumn[], active: DailyPositionsColumn[]) {
    setUpdatedActiveColumns(active);
  }

  function reset() {
    setUserOverrides(undefined);
  }

  function save() {
    const activeColumnsToSave = updatedActiveColumns ?? activeColumns;

    const userSettings: DailyPositionsReportUserOverride = {
      behavior: {
        groupByCommodity,
      },
      columns: {
        active: activeColumnsToSave,
      },
    };

    const userOverridesJson = JSON.stringify(userSettings);

    CommodityManagementApi.updateCustomerReportUserOverrides(undefined, customerReport.customerReportId, userOverridesJson);

    dispatch(
      setCustomerReportUserOverride({
        customerReportId: customerReport.customerReportId,
        userOverrides: userOverridesJson,
      }),
    );

    onSave({ ...customerReport, userOverrides: userOverridesJson });
  }

  return (
    <StoneXModal header={customerReport.reportName + ' Report Configuration'} width='850px' open={open} onClose={onClose}>
      <h2>Behavior</h2>

      <StoneXRow>
        <StoneXCheckbox
          label="Group rows by commodity"
          checked={groupByCommodity}
          onUpdate={setGroupByCommodity}
        />
      </StoneXRow>

      <StoneXDivider />

      <h2>Columns</h2>

      <p>Click on one or multiple items to select, then use the arrows to move.</p>

      <StoneXTransferList
        leftList={inactiveColumns}
        leftHeader="Available Columns"
        rightList={activeColumns}
        rightHeader="Active Columns"
        getId={(x) => x}
        getSortingOrder={(x) => columnOrderMap[x]}
        getOptionLabel={(x) => COLUMN_LABELS[x]}
        onUpdate={onTransferListUpdate}
      />

      <StoneXRow justify="end" marginDirection='top'>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button variant="outlined" onClick={reset}>Reset</Button>
        <Button onClick={save}>Set</Button>
      </StoneXRow>
    </StoneXModal>
  );
}
